/**
 * Hero Video Module
 *
 * Handles pause/play functionality for background videos in the hero component.
 *
 * @since 1.0.0
 */

/**
 * Initialize hero video pause/play controls
 *
 * Adds event listeners to pause buttons that toggle the playback
 * of background videos in hero sections.
 *
 * @since 1.0.0
 * @returns {void}
 */
export function initHeroVideo() {
	const pauseButtons = document.querySelectorAll( '.hero-video-pause' );

	// Exit early if no pause buttons are found.
	if ( 0 === pauseButtons.length ) {
		return;
	}

	/**
	 * Update button state to match video playback
	 *
	 * Updates the button text, ARIA labels, and CSS classes to reflect
	 * the current video playback state.
	 *
	 * @since 1.0.0
	 * @param {HTMLElement} button - The pause button element.
	 * @param {boolean} isPlaying - Whether the video is currently playing.
	 * @returns {void}
	 */
	function updateButtonState( button, isPlaying ) {
		const linkElement = button.querySelector( '.wp-block-button__link' );

		if ( isPlaying ) {
			linkElement.textContent = 'Pause';
			linkElement.setAttribute( 'aria-label', 'Pause background video' );
			button.setAttribute( 'aria-pressed', 'true' );
			button.classList.add( 'is-playing' );
		} else {
			linkElement.textContent = 'Play';
			linkElement.setAttribute( 'aria-label', 'Play background video' );
			button.setAttribute( 'aria-pressed', 'false' );
			button.classList.remove( 'is-playing' );
		}
	}

	/**
	 * Handle pause button click
	 *
	 * Toggles the video playback state and updates the button state
	 * for visual and accessibility feedback.
	 *
	 * @since 1.0.0
	 * @param {Event} event - The click/keydown event object.
	 * @returns {void}
	 */
	function handlePauseClick( event ) {
		event.preventDefault();

		const button = event.currentTarget;
		const heroSection = button.closest( '.hero-video' );

		// Exit early if hero section is not found.
		if ( ! heroSection ) {
			return;
		}

		const videoElement = heroSection.querySelector( '.wp-block-cover__video-background' );

		// Exit early if video element is not found.
		if ( ! videoElement ) {
			return;
		}

		// Toggle video playback state.
		if ( videoElement.paused ) {
			videoElement.play();
			updateButtonState( button, true );
		} else {
			videoElement.pause();
			updateButtonState( button, false );
		}
	}

	/**
	 * Handle keyboard events on the pause button
	 *
	 * Allows keyboard users to trigger play/pause using Enter or Space keys.
	 *
	 * @since 1.0.0
	 * @param {KeyboardEvent} event - The keydown event object.
	 * @returns {void}
	 */
	function handleKeyDown( event ) {
		// Only handle Enter or Space keys.
		if ( 'Enter' !== event.key && ' ' !== event.key ) {
			return;
		}

		handlePauseClick( event );
	}

	// Add click and keyboard listeners to all pause buttons.
	pauseButtons.forEach( ( button ) => {
		const heroSection = button.closest( '.hero-video' );

		// Exit early if hero section is not found.
		if ( ! heroSection ) {
			return;
		}

		const videoElement = heroSection.querySelector( '.wp-block-cover__video-background' );

		// Exit early if video element is not found.
		if ( ! videoElement ) {
			return;
		}

		// Set initial state and accessibility attributes.
		// Video autoplays by default, so button should show pause state.
		button.setAttribute( 'role', 'button' );
		button.setAttribute( 'tabindex', '0' );
		button.setAttribute( 'aria-pressed', 'true' );
		updateButtonState( button, true );

		// Add click and keyboard listeners to toggle playback.
		button.addEventListener( 'click', handlePauseClick );
		button.addEventListener( 'keydown', handleKeyDown );
	} );
}
